import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/admin';
import { keys, useWatch } from '../api/hooks';

/**
 * Полоса «вы смотрите чужое пространство» (FR-22).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * САМАЯ ВЕРХНЯЯ ПОЛОСА В КАБИНЕТЕ, И ЭТО НЕ ВОПРОС ОФОРМЛЕНИЯ.
 *
 * Все остальные полосы говорят о ДАННЫХ: подписка кончается, выгрузка
 * несвежая, ключ негоден. Эта говорит о том, ЧЬИ ЭТО ДАННЫЕ ВООБЩЕ.
 * Сотрудник, забывший, что он в чужом пространстве, разбирает чужие
 * кампании как свои — и это худшее, что может случиться с доверием
 * к сервису.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СЧЁТ ИДЁТ ОТ СРОКА С СЕРВЕРА, А НЕ ОТ ТАЙМЕРА В БРАУЗЕРЕ.
 *
 * Вкладка, пролежавшая ночь в спящем ноутбуке, не получит своих
 * тридцати тиков — счётчик в браузере показал бы «осталось 28 минут»
 * над кабинетом, к которому доступа давно нет. Отсюда пересчёт
 * от `expiresAt` на каждом тике: время идёт даже когда вкладка спала.
 *
 * КОГДА ВРЕМЯ ВЫШЛО — перезапрашиваем список пространств. Сервер
 * ответит уже без наблюдения, полоса исчезнет сама, а экран честно
 * покажет «нет доступа» вместо чужих чисел.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function WatchBand() {
  const qc = useQueryClient();
  const watch = useWatch().data ?? null;

  const [left, setLeft] = useState<number>(() => leftOf(watch?.expiresAt));

  useEffect(() => {
    if (watch === null) return;
    const tick = () => setLeft(leftOf(watch.expiresAt));
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [watch?.expiresAt, watch === null]);

  // Время вышло — спрашиваем сервер заново, а не решаем сами
  useEffect(() => {
    if (watch !== null && left <= 0) {
      void qc.invalidateQueries({ queryKey: keys.workspaces });
    }
  }, [left, watch, qc]);

  const leave = useMutation({
    mutationFn: () => adminApi.endImpersonation(watch!.id),
    onSuccess: () => {
      // Весь кабинет перечитывается: доступа больше нет ни к чему,
      // и оставить на экране чужие числа было бы хуже пустоты
      void qc.invalidateQueries();
    },
  });

  if (watch === null) return null;

  return (
    <div
      role="status"
      data-d="полоса-наблюдение"
      className="rk-band flex-wrap"
      style={{
        /**
         * ═══════════════════════════════════════════════════════════════
         * ЦВЕТ ВЗЯТ ЗАМЕРОМ СО СНИМКА «ПОД НАБЛЮДЕНИЕМ», А НЕ ПОДОБРАН.
         *
         * Здесь стоял цвет марки, вывернутый наизнанку, — рассуждением:
         * «это не поломка, а состояние, и полоса такого вида одна».
         * Рассуждение было верное, а цвет — нет. У дизайнера это
         * `--serious-wash` с чернилами `--serious-ink`: тот же ряд,
         * что и у остальных полос, но своя ступень тревоги.
         *
         * Полоса цвета марки читается как объявление сервиса — «у нас
         * новость». Эта полоса говорит не новость, а «вы не у себя»,
         * и цвет должен стоять в том же ряду, где «подписка кончается»
         * и «выгрузка несвежая», а не рядом с маркой.
         * ═══════════════════════════════════════════════════════════════
         */
        background: 'var(--serious-wash)',
        color: 'var(--serious-ink)',
        borderBottomColor: 'var(--line)',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ flex: '0 0 auto' }}>
        <path d="M8 3C4.4 3 1.6 5.4.5 8c1.1 2.6 3.9 5 7.5 5s6.4-2.4 7.5-5c-1.1-2.6-3.9-5-7.5-5zm0 8.2A3.2 3.2 0 1 1 8 4.8a3.2 3.2 0 0 1 0 6.4zm0-1.6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2z" />
      </svg>

      {/* ═══════════════════════════════════════════════════════════════
          ЖУРНАЛ НАЗВАН НА ЭКРАНЕ, И ЭТО ИЗ СНИМКА.

          У дизайнера строка кончается словами «всё сделанное пишется
          в журнал». Это не угроза сотруднику, а снятая неопределённость:
          человек, который знает, что его действия записаны, ведёт себя
          в чужом кабинете ровно так, как ведёт себя при владельце.

          «Только чтение» — наше и остаётся: у нас наблюдение и правда
          не даёт писать, и молчать об этом значит заставлять проверять
          нажатием.
          ═══════════════════════════════════════════════════════════ */}
      <span style={{ flex: '1 1 0%', minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>Вы в чужом пространстве: {watch.workspaceName}.</strong>{' '}
        Только чтение — изменить ничего нельзя. Клиент этого не видит: полосу видите только вы,
        всё сделанное пишется в журнал.
      </span>

      {/* ПРИЧИНА ПОКАЗАНА ТОМУ, КТО ЕЁ ПИСАЛ. Не для отчёта: увидев
          «разбираю обращение 4172», через двадцать минут вспоминаешь,
          зачем вообще сюда зашёл, — и выходишь, а не остаёшься */}
      <span style={{ opacity: 0.85 }}>· {watch.reason}</span>

      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{leftText(left)}</span>
        <button
          type="button"
          data-d="кнопка-выйти-из-наблюдения"
          className="rk-button-sm"
          style={{
            /**
             * ═══════════════════════════════════════════════════════════
             * КНОПКА ОБВЕДЕНА, А НЕ ЗАЛИТА — ТАК В СНИМКЕ.
             *
             * Залитая кнопка на полосе перетягивает взгляд на себя,
             * и первое, что человек видит, войдя в чужое пространство, —
             * кнопку выхода, а не строку о том, где он. Обводка
             * `currentColor` держит её на полосе, но не поперёд неё.
             *
             * Высота и поля тоже из снимка: `--ctl-h-xs`, `0 --sp-3`.
             * Стояли `2px 10px` — числом, мимо переменных.
             * ═══════════════════════════════════════════════════════════
             */
            flex: '0 0 auto',
            height: 'var(--ctl-h-xs)',
            padding: '0 var(--sp-3)',
            borderRadius: 'var(--r-sm)',
            border: 'var(--hair) solid currentColor',
            background: 'transparent',
            color: 'inherit',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          disabled={leave.isPending}
          onClick={() => leave.mutate()}
        >
          {/* НЕ ПРОСТО «ВЫЙТИ». В шапке, двадцатью пикселями выше, стоит
              кнопка «Выйти» — она выбрасывает из сервиса совсем. Два
              одинаковых слова рядом, означающие разное, однажды
              разлогинят посреди разбора обращения. */}
          {leave.isPending ? 'Выхожу…' : 'Выйти из наблюдения'}
        </button>
      </span>
    </div>
  );
}

/** Сколько осталось, в секундах. Отрицательного не бывает. */
export function leftOf(expiresAt: string | undefined): number {
  if (expiresAt === undefined) return 0;
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.round((t - Date.now()) / 1000));
}

/**
 * «Осталось 24 мин», «осталось 40 с».
 *
 * СЕКУНДЫ ПОЯВЛЯЮТСЯ ТОЛЬКО НА ПОСЛЕДНЕЙ МИНУТЕ. Тикающие секунды
 * все полчаса — это мигающая надпись над рабочим экраном; на последней
 * минуте они, наоборот, единственное, что успеет предупредить.
 */
export function leftText(seconds: number): string {
  if (seconds <= 0) return 'время вышло';
  if (seconds < 60) return `осталось ${seconds} с`;
  return `осталось ${Math.ceil(seconds / 60)} мин`;
}
